// ============================================================
// sessionApi — セッションのサーバ保存・一覧・復元（S2・2026-08-25）
//
// 正: booking-platform/md/ai-control-center.md §16-3
//
// サーバが正、localStorage はキャッシュ。保存はクリーン境界（idle/error）ごとに
// debounce して呼ばれるスナップショット全置換（メッセージ最大200件）。
// 「誰の作業か」はメッセージ単位の operator で持つ（§16-2 決定1）。
// ============================================================
import { getAgentAction, postAgentAction } from './api';
import { getOperatorName } from '../pages/reconcile/systemApi';
import type { AgentUiMessage, GeminiContent } from './types';

export interface SessionSummary {
  id: number;
  client_key: string;
  kind: string;
  anchor: string | null;
  title: string;
  operator: string;
  status: string;
  message_count: number;
  created_at: string;
  last_active_at: string;
}

/** セッション保存ペイロードを組み立てる（テスト可能な純関数） */
export interface SessionSaveMeta {
  kind?: 'work' | 'reservation';
  anchor?: string;
  /** 指定があればタイトルの自動生成より優先（予約セッションは「予約 XXX 〇〇様」を保つ） */
  title?: string;
}

export function buildSessionPayload(
  clientKey: string,
  uiMessages: AgentUiMessage[],
  contents: GeminiContent[],
  operatorName: string,
  meta: SessionSaveMeta = {},
) {
  // transient（開くたびに注入するタイムライン・メール文脈ブロック）は保存しない。
  // 保存すると次に開いたとき「保存分 + 新規注入」で二重になり、開くたびに雪だるま式に
  // 増えてタイムラインまで汚染する（2026-08-30 TR-CMGWL3 で16件→18件→20件と増殖した事象）
  const persistable = uiMessages.filter((m) => !(m as any).transient);
  return {
    client_key: clientKey,
    kind: meta.kind || 'work',
    anchor: meta.anchor || '',
    title: meta.title || deriveTitle(persistable),
    operator: operatorName,
    contents_json: JSON.stringify(stripInjectedContext(contents ?? [])),
    messages: persistable.map((m, i) => ({
      seq: i,
      // 発言者: user はオペレータ名、それ以外（assistant/activity/カード）はエージェント
      operator: m.kind === 'user' ? operatorName : 'エージェント',
      ui: JSON.stringify(m),
    })),
  };
}

/**
 * Gemini 履歴（contents）から注入済みの文脈ブロック（【システム】…）を取り除く。
 * タイムライン・起点メールの文脈は**開くたびに最新を注入し直す**ので、保存・復元の
 * 対象にすると開くたびに積み上がる。復元時と保存時の両方でこれを通す。
 */
export function stripInjectedContext(contents: GeminiContent[]): GeminiContent[] {
  const isSys = (part: any) => typeof part?.text === 'string' && part.text.startsWith('【システム】');
  return (contents || [])
    .filter((c: any) => !isSys(c?.parts?.[0]))
    // ユーザー発言に添えた【システム】パート（未送信下書きの状態など）も落とす。その時点の事実で、復元後は古い
    .map((c: any) => (Array.isArray(c?.parts) && c.parts.some(isSys)
      ? { ...c, parts: c.parts.filter((pt: any) => !isSys(pt)) } : c));
}

/** 一覧表示用タイトル: 最初のユーザー発言の先頭40文字 */
export function deriveTitle(uiMessages: AgentUiMessage[]): string {
  const first = uiMessages.find((m) => m.kind === 'user') as any;
  const text = String(first?.text ?? '').replace(/\s+/g, ' ').trim();
  return text === '' ? '(無題)' : text.slice(0, 40);
}

export async function saveSessionSnapshot(
  clientKey: string,
  uiMessages: AgentUiMessage[],
  contents: GeminiContent[],
  meta: SessionSaveMeta = {},
): Promise<void> {
  if (uiMessages.length === 0) return; // 空セッションは作らない
  const payload = buildSessionPayload(clientKey, uiMessages, contents, getOperatorName() || 'unknown', meta);
  try {
    await postAgentAction('session_save', payload);
  } catch (e: any) {
    // 保存失敗で会話は止めない。ただし黙らない（md/loop-engineering.md）
    // eslint-disable-next-line no-console
    console.error('[sessionApi] セッション保存に失敗（会話は継続します）:', e?.message || e);
  }
}

export async function listSessions(days = 3): Promise<SessionSummary[]> {
  const j: any = await getAgentAction('session_list', { kind: 'work', days });
  return j?.sessions || [];
}

/** 予約セッションを開く（無ければサーバが作る。1予約=1本） */
export async function openReservationSessionApi(contactCode: string, title: string): Promise<{
  session: SessionSummary & { contents_json?: string | null };
  uiMessages: AgentUiMessage[];
} | null> {
  const j: any = await postAgentAction('session_open_reservation', {
    contact_code: contactCode,
    title,
    operator: getOperatorName() || 'unknown',
  });
  if (!j?.success) return null;
  const uiMessages: AgentUiMessage[] = [];
  for (const m of j.messages || []) {
    try { uiMessages.push(JSON.parse(m.ui_json)); } catch { /* 壊れた1件で全体を落とさない */ }
  }
  return { session: j.session, uiMessages };
}

/** 予約タイムライン（4系統マージ・読み取り専用） */
export async function fetchReservationTimeline(code: string, rid?: string | number): Promise<{
  count: number;
  events: { ts: string; type: string; who: string; text: string; mail_id?: string }[];
} | null> {
  const j: any = await getAgentAction('reservation_timeline', { code, rid: rid != null ? String(rid) : '' });
  return j?.success ? { count: j.count, events: j.events || [] } : null;
}

export async function getSession(id: number): Promise<{
  session: SessionSummary & { contents_json: string | null };
  uiMessages: AgentUiMessage[];
  contents: GeminiContent[];
} | null> {
  const j: any = await getAgentAction('session_get', { id });
  if (!j?.success) return null;
  const uiMessages: AgentUiMessage[] = [];
  for (const m of j.messages || []) {
    try { uiMessages.push(JSON.parse(m.ui_json)); } catch { /* 壊れた1件で全体を落とさない */ }
  }
  let contents: GeminiContent[] = [];
  try { contents = JSON.parse(j.session?.contents_json || '[]') || []; } catch { contents = []; }
  return { session: j.session, uiMessages, contents };
}
